import {
    Platform,
    PlatformCertificateDocument,
    PlatformEmailTemplateOption,
    TableSorting,
    Task,
} from '@src/types'
import { api, ApiRes } from '@src/utils/api/client'
import { AxiosRequestConfig } from 'axios'

export interface PlatformsResponse {
    success: boolean
    platforms: Platform[]
    platforms_count: number
}

export interface PlatformResponse {
    success: string
    platform: Platform
}

export interface PlatformExportResponse {
    message: string
    tasks: Task[]
}

export interface PlatformOrderCertificateResponse {
    success: boolean
    certificates: PlatformCertificateDocument[]
}

export interface PlatformEmailTemplateListResponse {
    email_templates: PlatformEmailTemplateOption[]
}

export const getPlatforms = (
    filters?: object,
    page?: number,
    limit?: number,
    sorting?: TableSorting[]
): ApiRes<PlatformsResponse> => {
    const config: AxiosRequestConfig = {}

    config.params = {}

    if (filters && Object.keys(filters).length) {
        config.params = filters
    }

    if (typeof page !== 'undefined' && typeof limit !== 'undefined') {
        config.params['range'] = page * limit - limit + '-' + page * limit
    }

    if (sorting && sorting.length) {
        config.params['sort'] = sorting
            .map((sort) => {
                return (sort.direction === 'desc' ? '-' : '') + sort.field
            })
            .join(',')
    }

    return api.get('v2/manage/platforms', config)
}

export const getPlatform = (slug: string): ApiRes<PlatformResponse> =>
    api.get('v2/manage/platforms/' + slug)

export const createPlatform = (platform: Platform): ApiRes<PlatformResponse> =>
    api.post('v2/manage/platforms', platform)

export const updatePlatform = (platform: Platform): ApiRes<PlatformResponse> =>
    api.patch('v2/manage/platforms/' + platform.platform_name, platform)

export const deletePlatforms = (platforms: string[]): ApiRes<string> =>
    api.delete('v2/manage/platforms', { data: { platforms: platforms } })

export const deletePlatform = (slug: string): ApiRes<string> =>
    api.delete('manage/platforms/' + slug)

export const getEmailTemplatesList =
    (): ApiRes<PlatformEmailTemplateListResponse> =>
        api.get('v2/manage/platforms/email-templates')

export const apiExportPlatformRequestedMembershipDocumentsReport = (
    platform: Platform,
    type: string
): ApiRes<PlatformExportResponse> =>
    api.post(
        'manage/platforms/' +
            platform.platform_name +
            '/export-platform-requested-membership-documents-report',
        { lang: 'en', async: 1, type: type }
    )

export const apiExportOrderTransferHistory = (
    platform: Platform
): ApiRes<PlatformExportResponse> =>
    api.get(
        'manage/platforms/' +
            platform.platform_name +
            '/export-order-transfer-history',
        { params: { async: 1 } }
    )

export const sendEmailTemplateTest = (
    slug: string,
    template: string,
    campaign_id?: string
): ApiRes<[]> =>
    api.post(
        'manage/platforms/' + slug + '/mail-templates/' + template + '/send',
        { campaign_id: campaign_id }
    )

export const getPlatformOrderCertificates = (
    platform: Platform
): ApiRes<PlatformOrderCertificateResponse> => {
    return api.get(
        'v2/manage/platforms/' + platform.platform_name + '/order-certificates'
    )
}

export const storePlatformOrderCertificate = (
    platform: Platform,
    certificate: PlatformCertificateDocument
): ApiRes<PlatformOrderCertificateResponse> => {
    return api.post(
        'v2/manage/platforms/' + platform.platform_name + '/order-certificates',
        { certificate: certificate }
    )
}

export const deletePlatformOrderCertificates = (
    platform: Platform,
    certificates: string[]
): ApiRes<PlatformOrderCertificateResponse> => {
    return api.delete(
        'v2/manage/platforms/' + platform.platform_name + '/order-certificates',
        {
            data: { certificates: certificates },
        }
    )
}

export interface KycFolderExportResponse {
    message: string
    task: Task
}

export const triggerKycFolderExport = (
    platformName: string,
    filters?: Record<string, unknown>
): ApiRes<KycFolderExportResponse> => {
    const activeFilters = filters
        ? Object.fromEntries(
              Object.entries(filters).filter(
                  ([k, v]) =>
                      v !== null &&
                      v !== undefined &&
                      v !== '' &&
                      k !== 'platform_name_exact'
              )
          )
        : {}
    return api.post(
        'manage/platforms/' + platformName + '/export-investor-kyc-folder',
        Object.keys(activeFilters).length ? { filters: activeFilters } : {}
    )
}
